import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

const RECURRING_OPTIONS = [
  { value: "one_time", label: "One-time" },
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
];

const pmColors = {
  cash: "var(--color-success)",
  card: "var(--color-primary)",
  upi: "var(--color-accent)",
  bank_transfer: "var(--color-secondary)",
};

function SkeletonRow() {
  return (
    <tr className="border-b border-[var(--color-border)]">
      {[...Array(8)]?.map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-[var(--color-muted)] rounded animate-pulse" style={{ width: i === 0 ? "80px" : i === 7 ? "60px" : "90px" }} />
        </td>
      ))}
    </tr>
  );
}

function ExpenseModal({ open, onClose, onSaved, editData, categories, selectedBranchId }) {
  const today = new Date()?.toISOString()?.split("T")?.[0];
  const emptyForm = {
    title: "", category_id: "", amount: "", expense_date: today,
    payment_method: "cash", paid_to: "", description: "", recurring: "one_time",
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editData) {
      setForm({
        title: editData?.title || "",
        category_id: editData?.category_id || "",
        amount: editData?.amount ? String(editData?.amount) : "",
        expense_date: editData?.expense_date || today,
        payment_method: editData?.payment_method || "cash",
        paid_to: editData?.paid_to || "",
        description: editData?.description || "",
        recurring: editData?.recurring || "one_time",
      });
    } else {
      setForm(emptyForm);
    }
    setError(null);
  }, [editData, open]);

  const handleChange = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    if (!form?.title?.trim()) { setError("Title is required."); return; }
    if (!form?.amount || isNaN(parseFloat(form?.amount)) || parseFloat(form?.amount) <= 0) {
      setError("Please enter a valid amount."); return;
    }
    if (!form?.expense_date) { setError("Date is required."); return; }
    setSaving(true);
    try {
      const payload = {
        title: form?.title?.trim(),
        branch_id: selectedBranchId || null,
        category_id: form?.category_id || null,
        amount: parseFloat(form?.amount),
        expense_date: form?.expense_date,
        payment_method: form?.payment_method,
        paid_to: form?.paid_to?.trim() || null,
        description: form?.description?.trim() || null,
        recurring: form?.recurring,
      };
      if (editData?.id) {
        await api.put(`/expenses/${editData?.id}`, payload);
      } else {
        await api.post('/expenses', payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save expense.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)] sticky top-0 bg-[var(--color-card)] z-10">
          <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
            {editData ? "Edit Expense" : "Add Expense"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <p className="text-xs text-[var(--color-error)]">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Title *</label>
              <input
                value={form?.title}
                onChange={e => handleChange("title", e?.target?.value)}
                placeholder="e.g. Monthly Rent Payment"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Category</label>
              <select
                value={form?.category_id}
                onChange={e => handleChange("category_id", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                <option value="">Select category</option>
                {categories?.map(c => (
                  <option key={c?.id} value={c?.id}>{c?.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--color-muted-foreground)]">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form?.amount}
                  onChange={e => handleChange("amount", e?.target?.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Date *</label>
              <input
                type="date"
                value={form?.expense_date}
                onChange={e => handleChange("expense_date", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Payment Method</label>
              <select
                value={form?.payment_method}
                onChange={e => handleChange("payment_method", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                {PAYMENT_METHODS?.map(pm => (
                  <option key={pm?.value} value={pm?.value}>{pm?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Paid To</label>
              <input
                value={form?.paid_to}
                onChange={e => handleChange("paid_to", e?.target?.value)}
                placeholder="Vendor / Person name"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Recurring</label>
              <select
                value={form?.recurring}
                onChange={e => handleChange("recurring", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                {RECURRING_OPTIONS?.map(r => (
                  <option key={r?.value} value={r?.value}>{r?.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5">Description / Notes</label>
              <textarea
                value={form?.description}
                onChange={e => handleChange("description", e?.target?.value)}
                rows={3}
                placeholder="Additional notes or receipt details..."
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] sticky bottom-0 bg-[var(--color-card)]">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50"
          >
            {saving ? <Icon name="Loader2" size={14} color="white" className="animate-spin" /> : <Icon name="Check" size={14} color="white" />}
            {saving ? "Saving..." : editData ? "Update" : "Add Expense"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExpenseTracker({ onExpenseChange }) {
  useAuth();
  const { selectedBranchId } = useBranch();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [monthSummary, setMonthSummary] = useState({ thisMonth: 0, lastMonth: 0 });

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get('/expenses/categories');
      setCategories(data || []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/expenses', { params });
      setExpenses(data || []);

      // Monthly summary
      const now = new Date();
      const thisMonthStart = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, "0")}-01`;
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthStart = `${lastMonthDate?.getFullYear()}-${String(lastMonthDate?.getMonth() + 1)?.padStart(2, "0")}-01`;
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      const lastMonthEndStr = `${lastMonthEnd?.getFullYear()}-${String(lastMonthEnd?.getMonth() + 1)?.padStart(2, "0")}-${String(lastMonthEnd?.getDate())?.padStart(2, "0")}`;

      const thisMonth = (data || [])
        ?.filter(e => e?.expense_date >= thisMonthStart)
        ?.reduce((s, e) => s + parseFloat(e?.amount || 0), 0);
      const lastMonth = (data || [])
        ?.filter(e => e?.expense_date >= lastMonthStart && e?.expense_date <= lastMonthEndStr)
        ?.reduce((s, e) => s + parseFloat(e?.amount || 0), 0);
      setMonthSummary({ thisMonth, lastMonth });
    } catch (err) {
      console.error("Error fetching expenses:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    fetchCategories();
    fetchExpenses();
  }, [fetchCategories, fetchExpenses]);

  const handleSaved = () => {
    fetchExpenses();
    onExpenseChange?.();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/expenses/${deleteId}`);
      setDeleteId(null);
      fetchExpenses();
      onExpenseChange?.();
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = expenses?.filter(e => {
    const q = search?.toLowerCase();
    const matchSearch = !q || e?.title?.toLowerCase()?.includes(q) || e?.paid_to?.toLowerCase()?.includes(q);
    const matchCat = !filterCategory || e?.category_id === filterCategory;
    const matchMethod = !filterMethod || e?.payment_method === filterMethod;
    return matchSearch && matchCat && matchMethod;
  });

  const pctChange = monthSummary?.lastMonth > 0
    ? (((monthSummary?.thisMonth - monthSummary?.lastMonth) / monthSummary?.lastMonth) * 100)?.toFixed(1)
    : null;

  const fmt = v => `$${parseFloat(v || 0)?.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const hasFilters = search || filterCategory || filterMethod;

  return (
    <div className="space-y-5">
      {/* Monthly Summary Card */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)]">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center">
            <Icon name="Receipt" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <p className="text-xs text-[var(--color-muted-foreground)]">Total This Month</p>
            <p className="text-xl font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">{fmt(monthSummary?.thisMonth)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[var(--color-muted-foreground)]">vs last month:</span>
          <span className="font-medium text-[var(--color-foreground)]">{fmt(monthSummary?.lastMonth)}</span>
          {pctChange !== null && (
            <span className={`flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${parseFloat(pctChange) > 0 ? "bg-[var(--color-error)]/10 text-[var(--color-error)]" : "bg-[var(--color-success)]/10 text-[var(--color-success)]"}`}>
              <Icon name={parseFloat(pctChange) > 0 ? "TrendingUp" : "TrendingDown"} size={11} color={parseFloat(pctChange) > 0 ? "var(--color-error)" : "var(--color-success)"} />
              {Math.abs(parseFloat(pctChange))}%
            </span>
          )}
        </div>
        <button
          onClick={() => { setEditData(null); setModalOpen(true); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors whitespace-nowrap"
        >
          <Icon name="Plus" size={13} color="white" />
          Add Expense
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            placeholder="Search by title or paid to..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          />
        </div>
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e?.target?.value)}
          className="px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
        >
          <option value="">All Categories</option>
          {categories?.map(c => <option key={c?.id} value={c?.id}>{c?.name}</option>)}
        </select>
        <select
          value={filterMethod}
          onChange={e => setFilterMethod(e?.target?.value)}
          className="px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
        >
          <option value="">All Methods</option>
          {PAYMENT_METHODS?.map(pm => <option key={pm?.value} value={pm?.value}>{pm?.label}</option>)}
        </select>
        {hasFilters && (
          <button
            onClick={() => { setSearch(""); setFilterCategory(""); setFilterMethod(""); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--color-muted-foreground)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors whitespace-nowrap"
          >
            <Icon name="X" size={12} color="var(--color-muted-foreground)" />
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)] whitespace-nowrap">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Category</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-muted-foreground)]">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)] whitespace-nowrap">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)] whitespace-nowrap">Paid To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Notes</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[var(--color-muted-foreground)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)]?.map((_, i) => <SkeletonRow key={i} />)
              ) : filtered?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                        <Icon name="Receipt" size={22} color="var(--color-muted-foreground)" />
                      </div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        {hasFilters ? "No expenses match your filters" : "No expenses recorded yet"}
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">
                        {hasFilters ? "Try adjusting your search or filters" : "Click 'Add Expense' to record your first expense"}
                      </p>
                      {!hasFilters && (
                        <button
                          onClick={() => { setEditData(null); setModalOpen(true); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
                        >
                          <Icon name="Plus" size={12} color="white" />
                          Add Expense
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered?.map(expense => {
                  const cat = expense?.category_name ? { name: expense?.category_name, color: expense?.category_color, icon: expense?.category_icon } : null;
                  const pmLabel = PAYMENT_METHODS?.find(p => p?.value === expense?.payment_method)?.label || expense?.payment_method;
                  const pmColor = pmColors?.[expense?.payment_method] || "var(--color-muted-foreground)";
                  return (
                    <tr key={expense?.id} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] whitespace-nowrap">
                        {expense?.expense_date ? new Date(expense?.expense_date + "T00:00:00")?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-[var(--color-foreground)] leading-tight">{expense?.title}</p>
                          {expense?.recurring !== "one_time" && (
                            <span className="text-xs text-[var(--color-accent)] capitalize">{expense?.recurring}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {cat ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${cat?.color}22`, color: cat?.color }}
                          >
                            <Icon name={cat?.icon || "Tag"} size={10} color={cat?.color} />
                            {cat?.name}
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--color-muted-foreground)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-bold text-[var(--color-foreground)]">{fmt(expense?.amount)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${pmColor}18`, color: pmColor }}>
                          {pmLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)]">
                        {expense?.paid_to || "—"}
                      </td>
                      <td className="px-4 py-3 max-w-[160px]">
                        {expense?.description ? (
                          <p className="text-xs text-[var(--color-muted-foreground)] truncate" title={expense?.description}>{expense?.description}</p>
                        ) : (
                          <span className="text-xs text-[var(--color-muted-foreground)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => { setEditData(expense); setModalOpen(true); }}
                            className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
                            title="Edit"
                          >
                            <Icon name="Pencil" size={13} color="var(--color-muted-foreground)" />
                          </button>
                          <button
                            onClick={() => setDeleteId(expense?.id)}
                            className="p-1.5 rounded-md hover:bg-[var(--color-error)]/10 transition-colors"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={13} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && filtered?.length > 0 && (
          <div className="px-4 py-2.5 border-t border-[var(--color-border)] bg-[var(--color-muted)]/20 flex items-center justify-between">
            <p className="text-xs text-[var(--color-muted-foreground)]">{filtered?.length} expense{filtered?.length !== 1 ? "s" : ""}</p>
            <p className="text-xs font-medium text-[var(--color-foreground)]">
              Total: {fmt(filtered?.reduce((s, e) => s + parseFloat(e?.amount || 0), 0))}
            </p>
          </div>
        )}
      </div>

      <ExpenseModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditData(null); }}
        onSaved={handleSaved}
        editData={editData}
        categories={categories}
        selectedBranchId={selectedBranchId}
      />

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="Trash2" size={18} color="var(--color-error)" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[var(--color-foreground)]">Delete Expense</h4>
                <p className="text-xs text-[var(--color-muted-foreground)]">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-[var(--color-muted-foreground)] mb-5">Are you sure you want to delete this expense record?</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-error)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {deleting ? <Icon name="Loader2" size={14} color="white" className="animate-spin" /> : <Icon name="Trash2" size={14} color="white" />}
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
